import type {
  FullMultiLayerStack,
  FullSingleLayerStack,
  Series,
  Stack,
} from '../../../ViewportsConfigurations/types';
import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import type { FrameDataFragment, ImageParams } from 'generated/graphql';
import type { $AxisDirection } from '@kitware/vtk.js';
import type { DreAllViewportTypes } from 'config/constants';
import type {
  BaseImagingProviderArgs,
  EmitterEvent,
  ProviderStatus,
  SlicePlane,
} from './BaseImagingProvider';
import { BaseImagingProvider, emitterEvents } from './BaseImagingProvider';
import { getLPSDirections } from './utils';
import type { SupportedTextureTypes } from 'utils/textureUtils';
import { BaseSingleStackImagingProvider } from './BaseSingleStackImagingProvider';
import { encodeOverlayStackSmid } from '../../../Viewer/stackUtils';
import { logger } from 'modules/logger';
import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction';
import type { Handler } from 'mitt';

type LayerListenerRecord = {
  provider: BaseSingleStackImagingProvider;
  type: EmitterEvent;
  handler: Handler<unknown>;
};

// @ts-expect-error [EN-7967] - TS2344 - Type 'T' does not satisfy the constraint 'Readonly<FullBaseStack>'.
export type MultiStackImagingProviderArgs<T> = BaseImagingProviderArgs<T>;

export class MultiStackImagingProvider<
  T extends FullMultiLayerStack = FullMultiLayerStack,
> extends BaseImagingProvider<T> {
  #layerProviders: BaseImagingProvider<FullSingleLayerStack>[];

  #layerListeners: LayerListenerRecord[] = [];

  constructor(args: MultiStackImagingProviderArgs<T>) {
    super(args);

    this.providerType = 'multi-stack';
    this.#layerProviders = [];
  }

  dispose(): void {
    for (const { provider, type, handler } of this.#layerListeners) {
      provider.emitter.off(type, handler);
    }
    this.#layerListeners = [];
    this.#layerProviders = [];
    super.dispose();
  }

  getLayerProvider(layerNumber: number): BaseImagingProvider<FullSingleLayerStack> {
    return this.#layerProviders[layerNumber];
  }

  getConstituentProviders(): ReadonlyArray<BaseImagingProvider<FullSingleLayerStack>> {
    return this.#layerProviders;
  }

  getLayerProviderFromSopClasses(
    sopClass: readonly string[]
  ): Nullable<BaseImagingProvider<FullSingleLayerStack>> {
    return this.#layerProviders.find((provider) => {
      return sopClass.some((sopClassUid) => provider.isSopClass(sopClassUid));
    });
  }

  isSopClass(sopClass: string): boolean {
    return this.#layerProviders.every((provider) =>
      provider.stack.frames.every((frame) => frame.sopClassUID.startsWith(sopClass))
    );
  }

  hasValidDirection(): boolean {
    return this.#layerProviders[0].hasValidDirection();
  }

  setupStack() {}

  _loadStack(): Promise<void> {
    return Promise.resolve();
  }

  seriesAreHomogenous(): boolean {
    return false;
  }

  isStackMultiSOPClass(): boolean {
    return false;
  }

  get vtkImage(): vtkImageData {
    return this.#layerProviders[0].vtkImage;
  }

  getColorTransferFunctionForFrame(frameSmid: Nullable<string>): vtkColorTransferFunction | null {
    return this.#layerProviders[0].getColorTransferFunctionForFrame(frameSmid);
  }

  /* Map of the frame order in the series to the frame SMID */
  get frameSmidsMap(): {
    [key: number]: string;
  } {
    if (this.#layerProviders.length > 0) {
      return Object.fromEntries(
        (this.#layerProviders[0].stack.frames ?? []).map((frame, index) => [index, frame.smid])
      );
    }

    return {};
  }

  /* Number of slices / frames in the series */
  get stackSize(): number {
    if (this.#layerProviders.length > 0) {
      return this.#layerProviders.reduce((acc, provider) => {
        return (acc += provider.stackSize);
      }, 0);
    }
    return 0;
  }

  /* DICOM instance tags for this stack */
  get frameTags(): ReadonlyArray<FrameDataFragment> {
    if (this.#layerProviders.length > 0) {
      return this.#layerProviders[0].frameTags;
    }

    return [];
  }

  /**
   * General parameters for the image used in calculating transforms, positioning, etc.
   */
  get imageParams(): ImageParams {
    if (this.#layerProviders.length === 0) {
      throw new Error('Referenced providers have not been added');
    }

    return this.#layerProviders[0].imageParams;
  }

  getFrameAxes(): Set<$AxisDirection> {
    return this.#layerProviders[0].getFrameAxes();
  }

  hasPixels(): boolean {
    return true;
  }

  canRender(renderEngine: string): boolean {
    return true;
  }

  is3Dable(): boolean {
    if (this.#layerProviders.length > 0) {
      return this.#layerProviders.every((provider) => provider.is3Dable());
    }

    return false;
  }

  /**
   * Get the slice plane for the currently active slice of the given {viewType}.
   */
  getActiveSlicePlane(viewType: DreAllViewportTypes, viewportId: string): Nullable<SlicePlane> {
    const activeSlice = this.getActiveSlice(viewType, viewportId);
    return this.#layerProviders[0].getSlicePlaneAtViewIndex(viewType, activeSlice);
  }

  /**
   * Returns an object containing two vectors (normal and position vectors) for
   * the frame at the given {position} if the {position} is valid.
   *
   * Returns `null` for an invalid {position}
   */
  getSlicePlaneAtViewIndex(
    viewType: DreAllViewportTypes,
    position: number,
    layerIndex: number = 0
  ): Nullable<SlicePlane> {
    if (layerIndex > this.#layerProviders.length - 1 || layerIndex < 0) {
      throw new Error('Invalid layer index');
    }
    return this.#layerProviders[layerIndex].getSlicePlaneAtViewIndex(viewType, position);
  }

  getComputedStatus(): ProviderStatus {
    const statuses = this.#layerProviders.map((provider) => provider.status);

    if (statuses.includes('error')) {
      return 'error';
    } else if (statuses.some((s) => s === 'loading')) {
      return 'loading';
    } else if (statuses.every((status) => status === 'complete')) {
      return 'complete';
    }

    return 'init';
  }

  getReferencedSeries(): Series[] {
    // @ts-expect-error [EN-10263] - TS2322 - Type '({ readonly __typename?: "Series" | undefined; readonly smid: string; readonly description?: string | null | undefined; readonly seriesInstanceUID: string; readonly seriesDateTimeLocal?: DateTimeLocalScalarConfig | ... 1 more ... | undefined; ... 5 more ...; readonly imageRegistrations: readonly { ...; }[]; } | unde...' is not assignable to type '{ readonly __typename?: "Series" | undefined; readonly smid: string; readonly description?: string | null | undefined; readonly seriesInstanceUID: string; readonly seriesDateTimeLocal?: DateTimeLocalScalarConfig | null | undefined; ... 5 more ...; readonly imageRegistrations: readonly { ...; }[]; }[]'.
    return this.stack.stackLayers
      .map((layer) => {
        // @ts-expect-error [EN-7967] - TS2339 - Property 'series' does not exist on type '{ readonly __typename?: "LayeredStack"; readonly smid: string; readonly type: string; readonly study: { readonly __typename?: "Study"; readonly smid: string; }; readonly stackLayers: readonly { readonly __typename?: "Layer"; readonly stackSmid: string; readonly index: number; }[]; } | { ...; }'.
        const seriesSmid = this.study.stackedFrames.find((s) => s.smid === layer.stackSmid)?.series
          ?.smid;
        return this.study.seriesList.find((s) => s.smid === seriesSmid);
      })
      .filter(Boolean);
  }

  /** Create references to the providers that were configured in the multi-stack's `stackLayers` */
  calculateLayerProviders(stackProviders: Map<string, BaseImagingProvider<Stack>>) {
    // Get the ordered stackSmids of the stack layers, with the first as base and the second as the
    // layer
    const referencedSmids = this.stack.stackLayers.map((s) => s.stackSmid);
    const invalidStackSmids: Array<any> = [];

    // Clear any previously attached listeners before re-subscribing. Guards against
    // repeated calls accumulating handlers on layer emitters.
    for (const { provider, type, handler } of this.#layerListeners) {
      provider.emitter.off(type, handler);
    }
    this.#layerListeners = [];

    referencedSmids.forEach((stackSmid) => {
      const targetProvider = stackProviders.get(stackSmid);
      if (!targetProvider || !(targetProvider instanceof BaseSingleStackImagingProvider)) {
        invalidStackSmids.push(stackSmid);
        return;
      }

      const handleStatus: Handler<unknown> = () => {
        this.setStatus(this.getComputedStatus());
      };

      const handleFrameLoaded: Handler<unknown> = () => {
        this.emitter.emit(emitterEvents.frameLoaded, this.getFramesLoaded());

        this.viewRef.current?.requestRender();
      };
      targetProvider.emitter.on(emitterEvents.frameLoaded, handleFrameLoaded);
      this.#layerListeners.push({
        provider: targetProvider,
        type: emitterEvents.frameLoaded,
        handler: handleFrameLoaded,
      });

      const handleStackLoaded: Handler<unknown> = () => {
        this.emitter.emit(emitterEvents.stackLoaded);
      };
      targetProvider.emitter.on(emitterEvents.stackLoaded, handleStackLoaded);
      this.#layerListeners.push({
        provider: targetProvider,
        type: emitterEvents.stackLoaded,
        handler: handleStackLoaded,
      });

      targetProvider.emitter.on(emitterEvents.statusUpdated, handleStatus);
      this.#layerListeners.push({
        provider: targetProvider,
        type: emitterEvents.statusUpdated,
        handler: handleStatus,
      });

      const handleVolumeUpdated: Handler<unknown> = () => {
        this.emitter.emit(emitterEvents.volumeUpdated);
      };
      targetProvider.emitter.on(emitterEvents.volumeUpdated, handleVolumeUpdated);
      this.#layerListeners.push({
        provider: targetProvider,
        type: emitterEvents.volumeUpdated,
        handler: handleVolumeUpdated,
      });

      this.#layerProviders.push(targetProvider);
    });

    // Find the difference of the smids matched and the smids referenced in the stackLayer
    if (invalidStackSmids.length) {
      logger.error(
        `Stack smids from the stackLayer configuration could not be resolved a valid stack provider`,
        { invalidStackSmids }
      );
    }

    this.setStatus(this.getComputedStatus());

    // After we calculate the referenced providers, set up the LPS directions
    const { direction } = this.imageParams;
    const lpsdirections = getLPSDirections(direction);

    this.lpsDirections = lpsdirections;
  }

  /** Returns the original ImagingProvider rendering the stackSmid provided from the list of
   * referenced providers */
  getRefProviderByStackSmid(stackSmid: string): Nullable<BaseImagingProvider<Stack>> {
    return this.#layerProviders.find((provider) => provider.stack.smid === stackSmid);
  }

  getNumberOfColorChannels(frameIndex: number): number {
    return this.#layerProviders[0].getNumberOfColorChannels(frameIndex);
  }

  getVolume(): SupportedTextureTypes {
    return this.#layerProviders[0].getVolume();
  }

  getRange(): [number, number] | null {
    return this.#layerProviders[0].getRange();
  }

  getFramesLoaded(): number {
    return this.#layerProviders.reduce((acc, provider) => {
      return (acc += provider.getFramesLoaded());
    }, 0);
  }

  isFrameLoaded(frameSmid: string): boolean {
    return this.#layerProviders[0].isFrameLoaded(frameSmid);
  }

  isReadyToRender(): boolean {
    return this.#layerProviders.every((p) => p.isReadyToRender());
  }

  getImage(frameSmid?: string | null): vtkImageData | null {
    return this.#layerProviders[0].getImage(frameSmid);
  }

  getFramePixels(frameSmid: string): SupportedTextureTypes | null {
    return this.#layerProviders[0].getFramePixels(frameSmid);
  }

  estimateMemory(): number {
    return 0;
  }

  estimatePerFrameMemory(): number {
    return 0;
  }

  getFrameOfReference(): Nullable<string> {
    return this.#layerProviders[0].getFrameOfReference();
  }

  isLayeredStack(): boolean {
    return true;
  }

  getSeries(): Nullable<Series> {
    return this.#layerProviders[0].getSeries();
  }

  getLayeredVTKImage(): vtkImageData {
    return this.#layerProviders[1].vtkImage;
  }

  // mark each layer's current slice viewed and return the set of series smids
  // associated with those frames that are sufficiently reviewed, across all layers
  markSliceViewed(viewType: DreAllViewportTypes, slice: number): Set<string> {
    const baseLayerReviewedSeries = this.#layerProviders[0].markSliceViewed(viewType, slice);

    const reviewedSeries = new Set([...baseLayerReviewedSeries]);

    this.#layerProviders.slice(1).forEach((layerProvider) => {
      const layerSlicePlane = layerProvider?.getSlicePlaneAtViewIndex(viewType, slice);
      const layerSlice = layerProvider?.getSliceForWorldPosition(
        layerSlicePlane?.slicePosition,
        viewType
      );
      if (layerSlice != null) {
        layerProvider.markSliceViewed(viewType, layerSlice).forEach((reviewedSeriesForLayer) => {
          reviewedSeries.add(reviewedSeriesForLayer);
        });
      }
    });

    return reviewedSeries;
  }
  getViewportDisplayConfigKey({
    viewType,
    viewportId,
  }: {
    viewType: DreAllViewportTypes;
    viewportId: string;
  }): { stackSmid: string; viewType: DreAllViewportTypes; viewportId: string } {
    return {
      stackSmid: this.stack.smid,
      viewType: viewType,
      viewportId,
    };
  }

  getWindowLevelCacheKey({
    viewType,
    viewportId,
  }: {
    viewType: DreAllViewportTypes;
    viewportId: string;
  }): { seriesSmid: string; viewType: DreAllViewportTypes; viewportId: string } {
    const ct = this.#layerProviders[0];
    const pet = this.#layerProviders[1];
    const cacheKey = encodeOverlayStackSmid(ct?.stack?.smid ?? '', pet?.stack?.smid ?? '');
    return {
      seriesSmid: cacheKey,
      viewType,
      viewportId,
    };
  }
}
